import { useGetBuyerCertificateConsentQuery } from '@api/buyersCert.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { BlockContainer, BuyersCertificateConsentForm, Text, Title } from '@components';
import { Box, Grid, Skeleton } from '@mui/material';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { useLocation } from 'react-router-dom';

type BuyersCertificateConsentBlockProps = {
  title?: string;
  successMessage?: string;
};

export const BuyersCertificateConsentBlock: React.FC<BuyersCertificateConsentBlockProps> = ({
  title,
  successMessage,
}: BuyersCertificateConsentBlockProps) => {
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token') || '';
  const { currentData, isError, isLoading } = useGetBuyerCertificateConsentQuery(token);

  if (isLoading && !isError) {
    return <FormSkeletonLoader />;
  }

  if (isError || !currentData) {
    return (
      <Box sx={{ my: 4 }}>
        {title && (
          <Title variant='h2' sx={{ mb: 4, textAlign: 'center' }}>
            {title}
          </Title>
        )}
        <Text
          textAlign='center'
          component='span'
          fontSize={18}
          color={colors.error.main}
          display='block'
        >
          {t(TextCodes.BuyersCertificate.Consent.TokenError)}
        </Text>
      </Box>
    );
  }

  return (
    <BlockContainer>
      <div className='buyers-certificate-consent-block'>
        <BuyersCertificateConsentForm
          title={title}
          successMessage={successMessage}
          data={currentData?.data}
          token={token}
        />
      </div>
    </BlockContainer>
  );
};

const FormSkeletonLoader = () => {
  return (
    <Box sx={{ padding: 2 }}>
      <Grid
        container
        spacing={{ xs: 0, md: 3 }}
        sx={{
          justifyContent: 'center',
        }}
      >
        <Grid item xs={12} md={8} lg={6}>
          <Skeleton variant='rectangular' height={44} sx={{ mt: 2 }} />
          <Skeleton variant='rectangular' height={44} sx={{ mt: 2 }} />
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={4}>
              <Skeleton variant='rectangular' height={44} sx={{ mt: 2 }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Skeleton variant='rectangular' height={44} sx={{ mt: 2 }} />
            </Grid>
          </Grid>
          <Skeleton variant='rectangular' height={44} sx={{ mt: 2 }} />
          <Box sx={{ mt: 3 }}>
            <Skeleton variant='rectangular' height={44} width='40%' />
            <Skeleton variant='rectangular' height={44} width='40%' sx={{ mt: 2 }} />
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant='rectangular' width={320} height={56} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
